/**
 * Grill King scroll motion — Lenis smooth scroll + GSAP ScrollTrigger effects.
 *
 * Sits on top of fp-motion.js (which still owns every reveal-on-scroll) and
 * only adds what CSS cannot do: smoothed wheel scrolling and scroll-linked
 * scrubbing (hero scroll-out, image parallax, progress bar, magnetic CTAs,
 * clip-path media reveals, the expanding configurator banner, pergola preview
 * scale-up and word-by-word statement headings).
 *
 * Contract
 *   - Libraries are fetched after window load + idle, so nothing here
 *     competes with the LCP image or adds blocking time.
 *   - Every effect starts at its identity value, so the page never jumps when
 *     the script arrives mid-session.
 *   - prefers-reduced-motion: reduce exits before loading anything.
 *   - Lenis runs only for fine pointers (mouse / trackpad); touch keeps native
 *     scrolling. It pauses whenever a drawer, modal or menu locks the body.
 *
 * Config comes from window.gkMotionConfig, written in layout/theme.liquid.
 */
(function () {
  'use strict';

  var cfg = window.gkMotionConfig;
  if (!cfg) return;

  var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceQuery.matches) return;

  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var wantSmooth = !!cfg.smooth && finePointer;
  var wantFx = !!cfg.effects;
  if (!wantSmooth && !wantFx) return;

  var root = document.documentElement;
  var lenis = null;
  var lenisTick = null;
  var lockObserver = null;
  var active = true;

  /* Inline properties this file writes; teardown strips exactly these so no
     element is left mid-scrub (dim words, inset clip, shrunken media). */
  var FX_PROPS = [
    '--gk-hero-y', '--gk-hero-s', '--gk-copy-y', '--gk-copy-o', '--gk-py', '--gk-ps',
    '--gk-mx', '--gk-my', '--gk-type-s', '--gk-type-o', '--gk-media-s',
    'clip-path', 'opacity',
  ];
  var FX_NODES =
    '[data-gk-fx], [data-gk-magnetic], [data-gk-clip], [data-gk-expand], [data-gk-type], ' +
    '[data-gk-scale], .gk-word, .gk-hero-copy, .fp-hero__media, .gk-parallax__img';

  /* Scrollable overlays that must keep native wheel behaviour. */
  var LENIS_PREVENT =
    '[data-lenis-prevent], cart-drawer, .cart-drawer, .drawer, menu-drawer, .menu-drawer, ' +
    '#che-menu-overlay, .che-menu-overlay, predictive-search, .predictive-search, ' +
    'modal-dialog, .product-media-modal, quick-add-modal, .quick-add-modal, ' +
    '.mobile-facets, .facets__disclosure, .facet-filters, .fp-desktop-nav__submenu, ' +
    'select, textarea, iframe';

  var LOCK_CLASSES = ['noscroll', 'gk-search-lock', 'overflow-hidden', 'overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden-desktop'];

  /* Series card artwork has lettering baked into its edges, so it stays out of
     parallax (the over-scale would crop it) and only gets the clip reveal. */
  var PARALLAX_TARGETS = [
    { sel: '.egj-backyard-setups__media', amp: 6 },
    { sel: '.egj-pergola__media', amp: 7 },
    { sel: '.egj-pergola__atmosphere', amp: 10, scale: 1.2 },
    { sel: '.fp-reviews__bg', amp: 10, scale: 1.2 },
    { sel: '.image-with-text__media', amp: 6 },
    { sel: '.egj-pdp-entertain__media', amp: 6 },
    { sel: '.egj-pdp-specs__media', amp: 6 },
  ];

  var MAGNETIC_TARGETS = '.fp-hero__btn, .egj-config-banner__cta, .egj-pergola__cta--primary';

  var CLIP_TARGETS = '.egj-series-card__media, .egj-backyard-setups__media';
  var EXPAND_TARGETS = '.egj-config-banner';
  var BIG_TYPE_TARGETS = '.egj-config-banner__heading';
  /* Never the Design Studio iframe variant. */
  var SCALE_TARGETS = '.egj-pergola:not(.egj-pergola--has-iframe) .egj-pergola__media';
  /* Plain-text statement headings only; anything with child markup is skipped. */
  var WORD_TARGETS = '.fp-why .fp-section-heading, .fp-reviews__heading';

  /* ------------------------------------------------------------------ */
  /* Loading                                                             */
  /* ------------------------------------------------------------------ */

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = false;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function whenIdle(fn) {
    var run = function () {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(fn, { timeout: 1500 });
      } else {
        window.setTimeout(fn, 200);
      }
    };
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  /* ------------------------------------------------------------------ */
  /* Lenis                                                               */
  /* ------------------------------------------------------------------ */

  function isBodyLocked() {
    var body = document.body;
    for (var i = 0; i < LOCK_CLASSES.length; i++) {
      if (body.classList.contains(LOCK_CLASSES[i])) {
        // overflow-hidden-{breakpoint} only locks at its breakpoint; trust CSS.
        if (LOCK_CLASSES[i].indexOf('overflow-hidden-') === 0) {
          if (window.getComputedStyle(body).overflow === 'hidden') return true;
          continue;
        }
        return true;
      }
    }
    return window.getComputedStyle(body).overflowY === 'hidden';
  }

  function syncLock() {
    if (!lenis) return;
    if (isBodyLocked()) lenis.stop();
    else lenis.start();
  }

  function initLenis() {
    if (typeof window.Lenis !== 'function') return;

    lenis = new window.Lenis({
      lerp: 0.1,
      smoothWheel: true,
      wheelMultiplier: 1,
      allowNestedScroll: true,
      stopInertiaOnNavigate: true,
      anchors: false,
      prevent: function (node) {
        return !!(node && node.matches && node.matches(LENIS_PREVENT));
      },
    });

    window.gkLenis = lenis;

    if (window.gsap) {
      lenisTick = function (time) {
        if (lenis) lenis.raf(time * 1000);
      };
      window.gsap.ticker.add(lenisTick);
      window.gsap.ticker.lagSmoothing(0);
      if (window.ScrollTrigger) lenis.on('scroll', window.ScrollTrigger.update);
    } else {
      var raf = function (time) {
        if (!lenis) return;
        lenis.raf(time);
        window.requestAnimationFrame(raf);
      };
      window.requestAnimationFrame(raf);
    }

    lockObserver = new MutationObserver(syncLock);
    lockObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
    syncLock();
  }

  /* ------------------------------------------------------------------ */
  /* Effects                                                             */
  /* ------------------------------------------------------------------ */

  var gsap;
  var ST;
  var isSmall = window.matchMedia('(max-width: 749px)').matches;

  function initProgress() {
    if (document.querySelector('.gk-progress')) return;
    var bar = document.createElement('div');
    bar.className = 'gk-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    gsap.to(bar, {
      scaleX: 1,
      ease: 'none',
      scrollTrigger: { start: 0, end: 'max', scrub: 0.3 },
    });
  }

  function initHero(scope) {
    var hero = (scope || document).querySelector('.fp-hero');
    if (!hero || hero.dataset.gkFx === '1') return;
    // A scrubbed timeline renders at the current scroll position, so arming
    // it after the visitor has scrolled would visibly snap the hero.
    if (hero.getBoundingClientRect().top < -2) return;
    hero.dataset.gkFx = '1';

    var media = hero.querySelector('.fp-hero__media');
    var copy = hero.querySelectorAll('.fp-hero__inner, .fp-hero__bottom');
    Array.prototype.forEach.call(copy, function (el) {
      el.classList.add('gk-hero-copy');
    });

    var tl = gsap.timeline({
      scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true },
    });
    if (media) {
      tl.fromTo(
        media,
        { '--gk-hero-y': '0%', '--gk-hero-s': 1 },
        { '--gk-hero-y': isSmall ? '10%' : '18%', '--gk-hero-s': 1.1, ease: 'none' },
        0
      );
    }
    if (copy.length) {
      tl.fromTo(
        copy,
        { '--gk-copy-y': '0px', '--gk-copy-o': 1 },
        { '--gk-copy-y': isSmall ? '-40px' : '-90px', '--gk-copy-o': 0, ease: 'power1.in' },
        0
      );
    }
  }

  function initParallax(scope) {
    var host = scope || document;
    PARALLAX_TARGETS.forEach(function (cfgItem) {
      Array.prototype.forEach.call(host.querySelectorAll(cfgItem.sel), function (box) {
        if (box.dataset.gkFx === '1' || !belowFold(box)) return;
        var img = box.querySelector('img');
        if (!img || window.getComputedStyle(img).objectFit !== 'cover') return;
        box.dataset.gkFx = '1';
        box.classList.add('gk-parallax');
        img.classList.add('gk-parallax__img');

        var amp = isSmall ? Math.round(cfgItem.amp * 0.5) : cfgItem.amp;
        box.style.setProperty('--gk-ps', String(cfgItem.scale || 1.12));
        gsap.fromTo(
          box,
          { '--gk-py': -amp + '%' },
          {
            '--gk-py': amp + '%',
            ease: 'none',
            scrollTrigger: { trigger: box, start: 'top bottom', end: 'bottom top', scrub: true },
          }
        );
      });
    });
  }

  function initMagnetic(scope) {
    if (!finePointer) return;
    Array.prototype.forEach.call((scope || document).querySelectorAll(MAGNETIC_TARGETS), function (el) {
      if (el.dataset.gkMagnetic === '1') return;
      el.dataset.gkMagnetic = '1';
      el.classList.add('gk-magnetic');
      var strength = 0.18;

      el.addEventListener('pointermove', function (e) {
        if (!active) return;
        var r = el.getBoundingClientRect();
        gsap.to(el, {
          '--gk-mx': (e.clientX - (r.left + r.width / 2)) * strength + 'px',
          '--gk-my': (e.clientY - (r.top + r.height / 2)) * strength + 'px',
          duration: 0.35,
          ease: 'power3.out',
          overwrite: 'auto',
        });
      });
      el.addEventListener('pointerleave', function () {
        if (!active) return;
        gsap.to(el, {
          '--gk-mx': '0px',
          '--gk-my': '0px',
          duration: 0.7,
          ease: 'power3.out',
          overwrite: 'auto',
        });
      });
    });
  }

  /* The effects below start away from their identity value, so they are only
     armed for elements still below the fold — anything already on screen when
     the libraries arrive is left exactly as rendered. */
  function belowFold(el) {
    return el.getBoundingClientRect().top > window.innerHeight;
  }

  function each(host, sel, flag, fn) {
    Array.prototype.forEach.call(host.querySelectorAll(sel), function (el) {
      if (el.dataset[flag] === '1' || !belowFold(el)) return;
      el.dataset[flag] = '1';
      fn(el);
    });
  }

  function inset(pct, radius) {
    return 'inset(' + pct + '% ' + pct + '% ' + pct + '% ' + pct + '% round ' + radius + ')';
  }

  /* Media opens from a rounded, inset window to its full frame. */
  function initClipReveal(scope) {
    var amount = isSmall ? 4 : 7;
    each(scope || document, CLIP_TARGETS, 'gkClip', function (box) {
      var radius = window.getComputedStyle(box).borderTopLeftRadius || '0px';
      gsap.fromTo(
        box,
        { clipPath: inset(amount, '22px') },
        {
          clipPath: inset(0, radius),
          ease: 'none',
          scrollTrigger: { trigger: box, start: 'top bottom', end: 'top 55%', scrub: 0.6 },
        }
      );
    });
  }

  /* Dark banner grows from a floating card to full bleed while its headline
     scales up into place. */
  function initExpand(scope) {
    var host = scope || document;
    var side = isSmall ? 3 : 5;
    each(host, EXPAND_TARGETS, 'gkExpand', function (el) {
      gsap.fromTo(
        el,
        { clipPath: 'inset(0% ' + side + '% 0% ' + side + '% round 28px)' },
        {
          clipPath: 'inset(0% 0% 0% 0% round 0px)',
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'top 30%', scrub: 0.6 },
        }
      );
    });
    each(host, BIG_TYPE_TARGETS, 'gkType', function (el) {
      el.classList.add('gk-bigtype');
      gsap.fromTo(
        el,
        { '--gk-type-s': isSmall ? 0.88 : 0.8, '--gk-type-o': 0.2 },
        {
          '--gk-type-s': 1,
          '--gk-type-o': 1,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'center 55%', scrub: 0.6 },
        }
      );
    });
  }

  function initMediaScale(scope) {
    each(scope || document, SCALE_TARGETS, 'gkScale', function (el) {
      el.classList.add('gk-media-scale');
      gsap.fromTo(
        el,
        { '--gk-media-s': isSmall ? 0.94 : 0.86 },
        {
          '--gk-media-s': 1,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'center 60%', scrub: 0.6 },
        }
      );
    });
  }

  function splitWords(el) {
    if (el.children.length) return null;
    var words = el.textContent.trim().split(/\s+/);
    if (words.length < 2) return null;
    el.textContent = '';
    words.forEach(function (word, i) {
      var span = document.createElement('span');
      span.className = 'gk-word';
      span.textContent = word;
      el.appendChild(span);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
    });
    return el.querySelectorAll('.gk-word');
  }

  /* Statement headings light up word by word as they move through the
     viewport. */
  function initWords(scope) {
    each(scope || document, WORD_TARGETS, 'gkWords', function (el) {
      var words = splitWords(el);
      if (!words) return;
      gsap.fromTo(
        words,
        { opacity: 0.16 },
        {
          opacity: 1,
          ease: 'none',
          stagger: 0.1,
          scrollTrigger: { trigger: el, start: 'top 88%', end: 'bottom 50%', scrub: 0.5 },
        }
      );
    });
  }

  function scan(scope) {
    initHero(scope);
    initParallax(scope);
    initMagnetic(scope);
    initClipReveal(scope);
    initExpand(scope);
    initMediaScale(scope);
    initWords(scope);
  }

  function initFx() {
    gsap = window.gsap;
    ST = window.ScrollTrigger;
    if (!gsap || !ST) return;
    gsap.registerPlugin(ST);
    ST.config({ ignoreMobileResize: true });

    root.classList.add('gk-fx');
    initProgress();
    scan();

    var refreshTimer;
    function refreshSoon() {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(function () {
        if (active) ST.refresh();
      }, 150);
    }

    /* Facet and section swaps replace DOM wholesale; triggers bound to the
       old nodes would otherwise keep measuring detached elements. */
    function pruneDetached() {
      ST.getAll().forEach(function (t) {
        if (t.trigger && !t.trigger.isConnected) {
          if (t.animation) t.animation.kill();
          t.kill();
        }
      });
    }

    /* Lazy images and late-rendered sections shift layout after init. */
    document.addEventListener('load', function (e) {
      if (active && e.target && e.target.tagName === 'IMG') refreshSoon();
    }, true);
    document.addEventListener('facet:updated', function () {
      if (!active) return;
      pruneDetached();
      scan();
      refreshSoon();
    });
    document.addEventListener('shopify:section:load', function (e) {
      if (!active) return;
      pruneDetached();
      scan(e.target);
      refreshSoon();
    });
    document.addEventListener('shopify:section:unload', function (e) {
      if (!active) return;
      ST.getAll().forEach(function (t) {
        if (t.trigger && e.target.contains(t.trigger)) {
          if (t.animation) t.animation.kill();
          t.kill();
        }
      });
    });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refreshSoon);
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  function teardown() {
    active = false;
    if (lockObserver) {
      lockObserver.disconnect();
      lockObserver = null;
    }
    if (lenis) {
      if (lenisTick && window.gsap) window.gsap.ticker.remove(lenisTick);
      lenis.destroy();
      lenis = null;
      window.gkLenis = null;
    }
    if (window.ScrollTrigger) {
      window.ScrollTrigger.getAll().forEach(function (t) {
        if (t.animation) t.animation.kill();
        t.kill();
      });
    }
    var nodes = document.querySelectorAll(FX_NODES);
    if (window.gsap && nodes.length) window.gsap.killTweensOf(nodes);
    Array.prototype.forEach.call(nodes, function (el) {
      FX_PROPS.forEach(function (prop) {
        el.style.removeProperty(prop);
      });
    });
    root.classList.remove('gk-fx');
    var bar = document.querySelector('.gk-progress');
    if (bar) bar.remove();
  }

  whenIdle(function () {
    var chain = Promise.resolve();
    if (wantFx) {
      chain = chain
        .then(function () { return loadScript(cfg.src.gsap); })
        .then(function () { return loadScript(cfg.src.scrollTrigger); });
    }
    if (wantSmooth) {
      chain = chain.then(function () { return loadScript(cfg.src.lenis); });
    }
    chain
      .then(function () {
        if (reduceQuery.matches) return;
        if (wantFx) initFx();
        if (wantSmooth) initLenis();
      })
      .catch(function () {
        teardown();
      });

    if (typeof reduceQuery.addEventListener === 'function') {
      reduceQuery.addEventListener('change', function (e) {
        if (e.matches) teardown();
      });
    }
  });
})();
